import React, { useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import './MyAccountModal.css';

interface SectionProps {
  onBack: () => void;
}

const SecuritySettings = ({ onBack }: SectionProps) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleEnableEditing = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmEnableEditing = () => {
    setIsEditing(true);
    setShowConfirmModal(false);
  };

  const handleCancelEnableEditing = () => {
    setShowConfirmModal(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      if (newPassword !== confirmPassword) {
        throw new Error('New passwords do not match.');
      }

      if (newPassword.length < 8) {
        throw new Error('Password must be at least 8 characters long.');
      }

      // ⚙️ Update password through Supabase Auth
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      // ✅ Force sign-out after password change (best practice)
      await supabase.auth.signOut();

      setMessage('✅ Password updated successfully. Please sign in again.');
      setIsEditing(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('Password update error:', err);
      setMessage(`⚠️ ${err.message || 'Error updating password.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setMessage('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="my-account-section-content">
      <p className="profile-description">
        Update your password to keep your account secure. You’ll be signed out after the change
        and will need to log in again with your new password.
        <br /><br />
      </p>

      {!isEditing && (
        <div className="form-actions">
          <button className="save-btn" onClick={handleEnableEditing}>
            Click to Allow Editing
          </button>
          <button className="cancel-btn" onClick={onBack}>
            Back
          </button>
        </div>
      )}

      {isEditing && (
        <>
          <div className="profile-form">
            <label className="profile-label">Current Password:</label>
            <input
              type="password"
              className="profile-input"
              value={String(currentPassword)}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />

            <label className="profile-label">New Password:</label>
            <input
              type="password"
              className="profile-input"
              value={String(newPassword)}
              onChange={(e) => setNewPassword(e.target.value)}
            />

            <label className="profile-label">Confirm New Password:</label>
            <input
              type="password"
              className="profile-input"
              value={String(confirmPassword)}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          {message && <p className="profile-message">{message}</p>}

          <div className="form-actions">
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </>
      )}

      {/* ⚠️ Confirm Editing Modal */}
      {showConfirmModal && (
        <div className="edit-confirm-overlay">
          <div className="edit-confirm-modal">
            <p>
              Remember that this change will update your login credentials. Make sure your new
              password is secure and that you’ll remember it.
            </p>
            <div className="confirm-actions">
              <button
                className="confirm-btn"
                onClick={handleConfirmEnableEditing}
              >
                Continue
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancelEnableEditing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
